import { useRef, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { compressImage } from "../lib/imageCompression";
import { Label } from "./ui/label";
import { Camera, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const MIME_RE = /^image\/(jpe?g|png|webp|heic|heif)$/i;

export function ProofUpload({ onUploaded, testId }) {
  const { t } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null); // "optimizing" | "uploading"
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!MIME_RE.test(file.type || "")) return toast.error(t("payments.fileType"));
    if (file.size > 15 * 1024 * 1024) return toast.error(t("payments.fileSize"));

    setBusy(true);
    setDone(false);
    setStage("optimizing");
    let analytics;
    try {
      analytics = await compressImage(file, "DOC", setStage);
    } catch (err) {
      setBusy(false);
      setStage(null);
      setPreview(null);
      const code = err?.message;
      toast.error(code === "mime_invalid" ? t("payments.fileType") : t("payments.processError"));
      return; // never upload the original on compression failure
    }

    setPreview(URL.createObjectURL(analytics.file));
    setStage("uploading");
    try {
      const fd = new FormData();
      fd.append("file", analytics.file);
      fd.append("original_size", String(analytics.originalSize));
      fd.append("img_width", String(analytics.width));
      fd.append("img_height", String(analytics.height));
      const { data } = await api.post("/uploads/payment-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.data.file_id);
      setDone(true);
      toast.success(t("payments.uploaded"));
    } catch (err) {
      toast.error(extractError(err).message);
      setPreview(null);
      onUploaded(null);
    } finally {
      setBusy(false);
      setStage(null);
    }
  };

  const status = busy
    ? stage === "optimizing"
      ? <><Sparkles className="h-4 w-4 animate-pulse text-[#FF3B30]" /> {t("payments.optimizing")}</>
      : <><Loader2 className="h-4 w-4 animate-spin" /> {t("payments.uploading")}</>
    : done
      ? <><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t("payments.uploaded")}</>
      : t("payments.tapToUpload");

  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-sm">{t("payments.proofImage")}</Label>
      <input ref={ref} data-testid={testId} type="file" accept="image/*,image/heic,image/heif" onChange={pick} className="hidden" />
      <button type="button" data-testid={`${testId}-btn`} onClick={() => ref.current?.click()} disabled={busy}
        className="w-full rounded-xl border border-dashed border-white/15 bg-[#1C202E] hover:border-[#FF3B30]/60 transition p-4 flex items-center gap-4 text-left disabled:opacity-60">
        {preview ? <img src={preview} alt="preview" className="h-16 w-16 rounded-lg object-cover" /> : <span className="h-16 w-16 rounded-lg bg-white/5 flex items-center justify-center"><Camera className="h-6 w-6 text-slate-400" /></span>}
        <div className="flex-1 text-sm">
          <div className="text-white font-medium flex items-center gap-2" data-testid={`${testId}-status`}>{status}</div>
          <div className="text-xs text-slate-500 mt-0.5">{t("payments.proofHint")}</div>
        </div>
      </button>
    </div>
  );
}
